import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  BUILD_MANAGEMENT_ENTRY_LABEL,
  BUILD_MANAGEMENT_SECTIONS,
  type BuildManagementSectionId,
  isBuildManagementSectionId,
} from '../../types/buildManagement';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmDialog from '../ui/ConfirmDialog';

export type ManageBuildActionId =
  | 'add-part'
  | 'import'
  | 'enhance'
  | 'add-photos';

export interface ManageBuildShellProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: BuildManagementSectionId | string | null;
  vehicleTitle: string;
  completionPercent?: number;
  missingLabels?: string[];
  onTrack?: (event: string, metadata?: Record<string, unknown>) => void;
  /** Build-profile accent hex for the modal border. */
  accentColor?: string | null;
  /** Inline editors rendered under each section heading. */
  sectionPanels: Partial<Record<BuildManagementSectionId, React.ReactNode>>;
}

function sectionDomId(id: BuildManagementSectionId): string {
  return `manage-section-${id}`;
}

/** Section top relative to the scroll container’s content box. */
function sectionOffsetTop(root: HTMLElement, id: BuildManagementSectionId): number | null {
  const target = root.querySelector<HTMLElement>(`#${sectionDomId(id)}`);
  if (!target) return null;
  const rootRect = root.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return targetRect.top - rootRect.top + root.scrollTop;
}

function scrollContainerToSection(
  root: HTMLElement,
  id: BuildManagementSectionId,
  behavior: ScrollBehavior
) {
  const top = sectionOffsetTop(root, id);
  if (top == null) return;
  const next = Math.max(0, top - 8);
  try {
    if (typeof root.scrollTo === 'function') {
      root.scrollTo({ top: next, behavior });
    } else {
      root.scrollTop = next;
    }
  } catch {
    root.scrollTop = next;
  }
}

/** Last section whose top is at or above the scroll inset — works for tall sections like Profile. */
export function activeSectionFromScroll(
  root: HTMLElement,
  inset = 40
): BuildManagementSectionId {
  const y = root.scrollTop + inset;
  let current: BuildManagementSectionId = BUILD_MANAGEMENT_SECTIONS[0].id;
  for (const section of BUILD_MANAGEMENT_SECTIONS) {
    const top = sectionOffsetTop(root, section.id);
    if (top != null && top <= y) current = section.id;
  }
  return current;
}

const ManageBuildShell: React.FC<ManageBuildShellProps> = ({
  isOpen,
  onClose,
  initialSection = 'overview',
  vehicleTitle,
  onTrack,
  accentColor = null,
  sectionPanels,
}) => {
  const titleId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const suppressSpyRef = useRef(false);
  const suppressSpyTimerRef = useRef<number | null>(null);
  const resolvedInitial = useMemo(() => {
    if (initialSection && isBuildManagementSectionId(initialSection)) return initialSection;
    return 'overview';
  }, [initialSection]);
  const [activeSection, setActiveSection] = useState<BuildManagementSectionId>(resolvedInitial);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const wasOpenRef = useRef(false);
  const onTrackRef = useRef(onTrack);
  onTrackRef.current = onTrack;

  const requestClose = () => {
    if (isDirty) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  const armSpySuppression = (ms: number) => {
    suppressSpyRef.current = true;
    if (suppressSpyTimerRef.current != null) {
      window.clearTimeout(suppressSpyTimerRef.current);
    }
    suppressSpyTimerRef.current = window.setTimeout(() => {
      suppressSpyRef.current = false;
      suppressSpyTimerRef.current = null;
    }, ms);
  };

  const jumpToSection = (id: BuildManagementSectionId, behavior: ScrollBehavior = 'smooth') => {
    setActiveSection(id);
    armSpySuppression(behavior === 'smooth' ? 550 : 100);
    const root = scrollRef.current;
    if (!root) return;
    scrollContainerToSection(root, id, behavior);
  };

  // Seed section only when the shell opens (false → true).
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      setIsDirty(false);
      setConfirmClose(false);
      if (suppressSpyTimerRef.current != null) {
        window.clearTimeout(suppressSpyTimerRef.current);
        suppressSpyTimerRef.current = null;
      }
      suppressSpyRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setIsDirty(false);
    setConfirmClose(false);
    setActiveSection(resolvedInitial);
    onTrackRef.current?.('manage_build_opened', { section: resolvedInitial });
    const timer = window.setTimeout(() => {
      jumpToSection(resolvedInitial, 'auto');
    }, 50);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally seed once per open
  }, [isOpen, resolvedInitial]);

  useEffect(() => {
    if (!isOpen || !wasOpenRef.current) return;
    onTrackRef.current?.('manage_build_section_opened', { section: activeSection });
  }, [isOpen, activeSection]);

  // Scroll-position spy (not IntersectionObserver) so tall Profile keeps the nav in sync.
  useEffect(() => {
    if (!isOpen) return undefined;
    const root = scrollRef.current;
    if (!root) return undefined;

    let frame = 0;
    const onScroll = () => {
      if (suppressSpyRef.current) return;
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = activeSectionFromScroll(root);
        setActiveSection((prev) => (prev === next ? prev : next));
      });
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      root.removeEventListener('scroll', onScroll);
    };
  }, [isOpen]);

  // Mark shell dirty when any form control inside changes (unsaved-exit guard).
  useEffect(() => {
    if (!isOpen) return undefined;
    const root = scrollRef.current;
    if (!root) return undefined;
    const markDirty = () => setIsDirty(true);
    root.addEventListener('input', markDirty, true);
    root.addEventListener('change', markDirty, true);
    return () => {
      root.removeEventListener('input', markDirty, true);
      root.removeEventListener('change', markDirty, true);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const navButtonClass = (selected: boolean, danger?: boolean) =>
    `text-left min-h-[44px] px-3 py-2 rounded-lg text-sm transition-colors ${
      selected ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
    } ${danger ? 'md:mt-2' : ''}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={requestClose}
      size="xl"
      title={BUILD_MANAGEMENT_ENTRY_LABEL}
      description={`Edit ${vehicleTitle} without digging through menus.`}
      showClose
      closeOnOverlayClick={!isDirty}
      initialFocus="dialog"
      className="manage-build-modal w-full !max-w-5xl !overflow-hidden flex flex-col h-[100dvh] max-h-[100dvh] rounded-none p-4 sm:h-auto sm:max-h-[92vh] sm:rounded-xl sm:p-6"
      style={
        accentColor
          ? ({
              borderColor: accentColor,
              boxShadow: `0 0 0 1px ${accentColor}55, 0 24px 60px -24px rgba(0, 0, 0, 0.75)`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div className="flex flex-col min-h-0 flex-1 gap-3 md:gap-4">
        <nav aria-label="Build management sections" className="shrink-0 md:hidden">
          <ul className="flex gap-1 overflow-x-auto overscroll-x-contain -mx-1 px-1 pb-1">
            {BUILD_MANAGEMENT_SECTIONS.map((item) => {
              const selected = item.id === activeSection;
              return (
                <li key={item.id} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => jumpToSection(item.id)}
                    className={`${navButtonClass(selected)} whitespace-nowrap min-h-[40px]`}
                    aria-current={selected ? 'true' : undefined}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-4 md:gap-6 min-h-0 flex-1">
          <nav
            aria-label="Build management sections"
            className="hidden md:block min-h-0 overflow-y-auto border-r border-white/[0.06] pr-3"
          >
            <ul className="flex flex-col gap-1">
              {BUILD_MANAGEMENT_SECTIONS.map((item) => {
                const selected = item.id === activeSection;
                return (
                  <li key={`desk-${item.id}`} className="w-full">
                    <button
                      type="button"
                      onClick={() => jumpToSection(item.id)}
                      className={`w-full ${navButtonClass(selected, item.danger)}`}
                      aria-current={selected ? 'true' : undefined}
                    >
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div
            ref={scrollRef}
            className="min-w-0 min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]"
            tabIndex={-1}
            role="region"
            aria-labelledby={titleId}
          >
            <h3 id={titleId} className="sr-only">
              Manage build sections
            </h3>

            {BUILD_MANAGEMENT_SECTIONS.map((item) => (
              <section
                key={item.id}
                id={sectionDomId(item.id)}
                aria-labelledby={`${sectionDomId(item.id)}-heading`}
                className="scroll-mt-3 pb-10 mb-8 border-b border-white/[0.06] last:border-b-0 last:mb-0 last:pb-4"
              >
                <header className="mb-4">
                  <h4
                    id={`${sectionDomId(item.id)}-heading`}
                    className={`text-lg font-semibold ${item.danger ? 'text-red-300' : 'text-white'}`}
                  >
                    {item.label}
                  </h4>
                  {item.description ? (
                    <p className="text-sm text-gray-400 mt-1">{item.description}</p>
                  ) : null}
                </header>

                {sectionPanels[item.id] ?? (
                  <p className="text-sm text-gray-500">Nothing to edit in this section yet.</p>
                )}
              </section>
            ))}

            <div className="pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] flex justify-end gap-2">
              {isDirty ? (
                <p className="text-xs text-amber-300/90 self-center mr-auto" role="status">
                  Unsaved edits — press Save in that section, then Done.
                </p>
              ) : null}
              <Button type="button" variant="ghost" onClick={requestClose} className="min-h-[44px]">
                Done
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmClose}
        title="Leave Manage Build?"
        description="You may have unsaved edits in a section. Closing now discards anything not saved in that section."
        confirmLabel="Leave without saving"
        cancelLabel="Keep editing"
        destructive
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => {
          setConfirmClose(false);
          setIsDirty(false);
          onClose();
        }}
      />
    </Modal>
  );
};

export default ManageBuildShell;
